import React, { useEffect, useId, useState } from 'react';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const QUOTE_API = '/api/quote';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName?: string;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ isOpen, onClose, productName = '' }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    product: productName,
    message: ''
  });

  useEffect(() => {
    if (isOpen) setFormData((previous) => ({ ...previous, product: productName }));
  }, [isOpen, productName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const res = await fetch(QUOTE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string }).error || res.statusText || 'Request failed');
      }
      setStep('success');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('form');
    setSubmitError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Modal Content */}
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="quote-modal relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
        <button
          onClick={handleClose}
          aria-label={t('quote.modal.close')}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        <div className="p-8">
          {step === 'form' ? (
            <>
              <div className="mb-6">
                <h2 id={titleId} className="text-2xl font-bold text-gray-900">{t('quote.modal.title')}</h2>
                <p className="text-gray-500 mt-1">{t('quote.modal.subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor={`${titleId}-name`} className="block text-sm font-medium text-gray-700 mb-1">{t('quote.modal.fullName')}</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder={t('quote.modal.placeholderName')}
                    id={`${titleId}-name`}
                    autoComplete="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`${titleId}-email`} className="block text-sm font-medium text-gray-700 mb-1">{t('quote.modal.email')}</label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                      placeholder={t('quote.modal.placeholderEmail')}
                    id={`${titleId}-email`}
                    autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label htmlFor={`${titleId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">{t('quote.modal.phone')}</label>
                    <input
                      type="tel"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                      placeholder={t('quote.modal.placeholderPhone')}
                    id={`${titleId}-phone`}
                    autoComplete="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor={`${titleId}-product`} className="block text-sm font-medium text-gray-700 mb-1">{t('quote.modal.productInterest')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder={t('quote.modal.placeholderProduct')}
                    id={`${titleId}-product`}

                    value={formData.product}
                    onChange={(e) => setFormData({...formData, product: e.target.value})}
                  />
                </div>

                <div>
                  <label htmlFor={`${titleId}-message`} className="block text-sm font-medium text-gray-700 mb-1">{t('quote.modal.message')}</label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none"
                    placeholder={t('quote.modal.placeholderMessage')}
                    id={`${titleId}-message`}

                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  ></textarea>
                </div>

                {submitError && (
                  <p className="text-red-600 text-sm" role="alert">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center mt-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('quote.modal.submit')}
                    </>
                  ) : (
                    <>
                      {t('quote.modal.submit')} <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center animate-fade-in">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 id={titleId} className="text-2xl font-bold text-gray-900 mb-2">{t('quote.modal.successTitle')}</h2>
              <p className="text-gray-500 mb-6">
                {t('quote.modal.successMessage')}
              </p>
              <button
                onClick={handleClose}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                {t('quote.modal.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
